import { useCallback, useEffect, useState } from 'react';
import { useGetEnterpriseOrgStatusQuery } from '@consolidocs-ui/data-access/CompanyApi';
import { EnterpriseOrgStatusType } from '@consolidocs-ui/types';

/** How often `GET /enterprise-org-status` is re-fetched while provisioning. */
export const STATUS_POLL_INTERVAL_MS = 5000;

/**
 * How long we keep polling before giving up on the UI. Provisioning continues
 * on the backend either way — the timeout only stops an endless poll loop and
 * lets the user leave the wizard.
 */
export const STATUS_POLL_TIMEOUT_MS = 3 * 60 * 1000;

/** The four backend-provisioned resources, in the order they are displayed. */
export const PROVISION_FLAGS = [
  'isDbCreated',
  'isCognitoPoolCreated',
  'isSqsReady',
  'isServiceReady',
] as const;

export type ProvisionFlag = (typeof PROVISION_FLAGS)[number];

/**
 * Backend provisioning states that will not progress on their own. The
 * backend accepts `POST /organizations/{id}/retry-provisioning` from these.
 */
export const FAILED_PROVISIONING_STATUSES = [
  'START_FAILED',
  'FAILED',
  'FAULT',
  'STOPPED',
  'TIMED_OUT',
] as const;

/**
 * The status endpoint may return a bare array or a `{ data: [...] }` wrapper —
 * normalise to a list before looking up an organization.
 */
export const toEnterpriseOrgStatusList = (
  data: unknown
): EnterpriseOrgStatusType[] =>
  Array.isArray(data)
    ? data
    : (data as { data?: EnterpriseOrgStatusType[] } | undefined)?.data ?? [];

export const isProvisioningFailed = (provisioningStatus?: string): boolean =>
  (FAILED_PROVISIONING_STATUSES as readonly string[]).includes(
    provisioningStatus ?? ''
  );

export interface EnterpriseOrgStatusResult {
  /** The organization's row from the status endpoint, once it appears. */
  status?: EnterpriseOrgStatusType;
  /** Every provisioning flag, defaulted to `false` until the row shows up. */
  flags: Record<ProvisionFlag, boolean>;
  /** True once all four resources are ready. */
  isComplete: boolean;
  /** True while a poll cycle is active (i.e. still waiting on the backend). */
  isPolling: boolean;
  /** True when we stopped polling before everything was ready. */
  isTimedOut: boolean;
  /** True when the infrastructure build failed and needs a retry. */
  isFailed: boolean;
  /** Set when the status endpoint itself failed. */
  error: unknown;
  /** Starts a fresh watch, e.g. after provisioning has been retried. */
  restart: () => Promise<void>;
}

/**
 * Polls the enterprise provisioning status of a single organization.
 *
 * Polling is skipped entirely without an `orgId` or when `enabled` is false,
 * and stops as soon as the org is fully provisioned, its build fails, or the
 * timeout is hit, so a general (non-enterprise) organization never hits this
 * endpoint.
 */
export const useEnterpriseOrgStatus = (
  orgId?: string,
  { enabled = true }: { enabled?: boolean } = {}
): EnterpriseOrgStatusResult => {
  const isActive = enabled && !!orgId;
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [hasStopped, setHasStopped] = useState(false);
  // Bumped by `restart` so the timeout below starts counting again.
  const [watchId, setWatchId] = useState(0);

  const { data, error, refetch } = useGetEnterpriseOrgStatusQuery(undefined, {
    skip: !isActive,
    pollingInterval: isActive && !hasStopped ? STATUS_POLL_INTERVAL_MS : 0,
    refetchOnMountOrArgChange: true,
  });

  const statusList = toEnterpriseOrgStatusList(data);

  const status = orgId
    ? statusList.find((org) => org.id === orgId)
    : undefined;

  const flags = PROVISION_FLAGS.reduce(
    (acc, flag) => ({ ...acc, [flag]: !!status?.[flag] }),
    {} as Record<ProvisionFlag, boolean>
  );

  const isComplete = !!status && PROVISION_FLAGS.every((flag) => flags[flag]);
  const isFailed = !isComplete && isProvisioningFailed(status?.provisioningStatus);

  // Give up after the timeout — provisioning keeps running on the backend.
  useEffect(() => {
    if (!isActive || isComplete) return;
    const timer = setTimeout(() => setIsTimedOut(true), STATUS_POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isActive, isComplete, watchId]);

  // Stop the poll once there is nothing left to wait for. A failed build and a
  // failing status endpoint are terminal too — polling only spams the backend.
  useEffect(() => {
    if (isComplete || isTimedOut || isFailed || error) setHasStopped(true);
  }, [isComplete, isTimedOut, isFailed, error]);

  // A different organization starts a fresh watch.
  useEffect(() => {
    setIsTimedOut(false);
    setHasStopped(false);
  }, [orgId]);

  // Refetch first so the stale failed row is gone before polling resumes;
  // otherwise the stop effect above would end the new watch straight away.
  const restart = useCallback(async () => {
    if (!isActive) return;
    await refetch();
    setIsTimedOut(false);
    setHasStopped(false);
    setWatchId((id) => id + 1);
  }, [isActive, refetch]);

  return {
    status,
    flags,
    isComplete,
    isPolling: isActive && !isComplete && !isTimedOut && !isFailed && !error,
    isTimedOut: isActive && isTimedOut && !isComplete && !isFailed,
    isFailed: isActive && isFailed,
    error,
    restart,
  };
};
